import React, { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { Play, Square, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTimerStore } from '@/store/timerStore'
import { timeEntryService } from '@/services/timeEntryService'
import { refreshActiveTimer } from '@/lib/useActiveTimerSync'
import TimerStopDialog from './TimerStopDialog'

const formatElapsed = (startTime) => {
  const secs = Math.floor((Date.now() - new Date(startTime)) / 1000)
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const formatDuration = (startTime) => {
  const secs = Math.floor((Date.now() - new Date(startTime)) / 1000)
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

const TimeTracker = ({ taskId, projectId, taskTitle }) => {
  const queryClient = useQueryClient()
  const { activeTimer, startTimer, stopTimer } = useTimerStore()
  const [elapsed, setElapsed] = useState('')
  const [loading, setLoading] = useState(false)
  const [stopDialogOpen, setStopDialogOpen] = useState(false)
  // Tracks if there's a conflicting active timer on another task
  const [conflictTimer, setConflictTimer] = useState(null)

  const isRunning = activeTimer?.taskId === taskId

  // Tick elapsed time
  useEffect(() => {
    if (!isRunning || !activeTimer?.startTime) return
    const interval = setInterval(() => setElapsed(formatElapsed(activeTimer.startTime)), 1000)
    setElapsed(formatElapsed(activeTimer.startTime))
    return () => clearInterval(interval)
  }, [isRunning, activeTimer?.startTime])

  const handleStart = async () => {
    // If another task has a running timer, show conflict stop dialog
    if (activeTimer && activeTimer.taskId !== taskId) {
      setConflictTimer(activeTimer)
      setStopDialogOpen(true)
      return
    }
    setLoading(true)
    try {
      const res = await timeEntryService.start(taskId)
      startTimer({
        id: res.data.activeTimer.id,
        taskId,
        taskTitle,
        projectId,
        projectName: '',
        startTime: res.data.activeTimer.start_time
      })
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      toast.success('Timer started')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start timer')
    } finally {
      setLoading(false)
    }
  }

  const handleStopConfirm = async (note) => {
    setLoading(true)
    try {
      // If stopping a conflict timer and then starting this one
      if (conflictTimer) {
        await timeEntryService.stop(conflictTimer.id, note)
        const res = await timeEntryService.start(taskId)
        startTimer({
          id: res.data.activeTimer.id,
          taskId,
          taskTitle,
          projectId,
          projectName: '',
          startTime: res.data.activeTimer.start_time
        })
        toast.success('Previous timer stopped, new timer started')
      } else {
        await timeEntryService.stop(activeTimer.id, note)
        stopTimer()
        toast.success('Timer stopped')
      }
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      setStopDialogOpen(false)
      setConflictTimer(null)
    } catch (err) {
      if (err.response?.status === 404) {
        // Already stopped elsewhere (another tab, or an AI agent via MCP)
        refreshActiveTimer(queryClient)
        setStopDialogOpen(false)
        toast.info('That timer was already stopped elsewhere')
        return
      }
      toast.error(err.response?.data?.error || 'Failed to stop timer')
    } finally {
      setLoading(false)
    }
  }

  const handleStop = () => setStopDialogOpen(true)

  return (
    <>
      <div className="flex items-center gap-2">
        {isRunning ? (
          <>
            <span className="text-sm font-mono font-medium text-primary tabular-nums">{elapsed}</span>
            <Button size="sm" variant="outline" onClick={handleStop} disabled={loading} className="h-7 text-xs border-destructive text-destructive hover:bg-destructive hover:text-white">
              <Square className="h-3 w-3 mr-1 fill-current" />
              Stop
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={handleStart} disabled={loading} className="h-7 text-xs">
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1 fill-current" />}
            Start Timer
          </Button>
        )}
      </div>

      <TimerStopDialog
        open={stopDialogOpen}
        onOpenChange={setStopDialogOpen}
        taskTitle={conflictTimer ? conflictTimer.taskTitle : taskTitle}
        elapsedLabel={activeTimer ? formatDuration(activeTimer.startTime) : ''}
        onConfirm={handleStopConfirm}
        loading={loading}
      />
    </>
  )
}

export default TimeTracker
